import React from 'react';
import PropTypes from 'prop-types';
import './Skeleton.scss';

/**
 * Base Skeleton component - the foundational building block for all skeleton loaders.
 *
 * This component renders a placeholder rectangle with shimmer animation that serves
 * as the base for all other skeleton patterns. It's designed to be lightweight,
 * performant, and visually consistent with the application's theme.
 *
 * @component
 * @example
 * // Basic usage
 * <Skeleton width="100%" height="20px" />
 *
 * @example
 * // Circular skeleton for avatars
 * <Skeleton width="40px" height="40px" borderRadius="50%" />
 *
 * @example
 * // Custom styling
 * <Skeleton
 *   width="200px"
 *   height="16px"
 *   borderRadius="4px"
 *   className="custom-skeleton"
 * />
 */
// Design token defaults (matching _variables.scss)
const SKELETON_DEFAULTS = {
  height: '20px', // $skeleton-text-height-2xl
  borderRadius: '4px', // $skeleton-border-radius-sm
};

const Skeleton = ({
  width = '100%',
  height = SKELETON_DEFAULTS.height,
  borderRadius = SKELETON_DEFAULTS.borderRadius,
  className = '',
  style = {},
  ...props
}) => {
  return (
    <div
      className={`skeleton ${className}`}
      style={{
        width,
        height,
        borderRadius,
        ...style,
      }}
      {...props}
    />
  );
};

Skeleton.propTypes = {
  /** Width of the skeleton element */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Height of the skeleton element */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Border radius for rounded corners */
  borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Additional CSS classes */
  className: PropTypes.string,

  /** Additional inline styles */
  style: PropTypes.object,
};

export default Skeleton;
